import React, { useState, useEffect } from 'react';
import { PlusIcon, CalendarIcon, InformationCircleIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';

const AddPhaseButton = ({ projectId, onPhaseAdded }) => {
  const [showModal, setShowModal] = useState(false);
  const [phaseName, setPhaseName] = useState('');
  const [phaseDescription, setPhaseDescription] = useState('');
  const [phaseDueDate, setPhaseDueDate] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nextOrderIndex, setNextOrderIndex] = useState(null);
  const [loadingOrderIndex, setLoadingOrderIndex] = useState(false);

  // Fetch the next order index when the modal opens
  useEffect(() => {
    const fetchNextOrderIndex = async () => {
      if (!showModal || !projectId) return;
      
      try {
        setLoadingOrderIndex(true);
        const response = await api.get(`/phases?project_id=${projectId}`);
        if (response.data && response.data.phases) {
          const phases = response.data.phases;
          if (phases.length === 0) {
            setNextOrderIndex(0);
          } else {
            // Find the highest order_index and add 1
            const highestIndex = Math.max(...phases.map(phase => phase.order_index));
            setNextOrderIndex(highestIndex + 1);
          }
        }
      } catch (error) {
        console.error('Error fetching phases for order index:', error);
      } finally {
        setLoadingOrderIndex(false);
      }
    };
    
    fetchNextOrderIndex();
  }, [showModal, projectId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!phaseName.trim()) {
      toast.error('Phase name is required');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const response = await api.post(`/phases`, {
        project_id: projectId,
        name: phaseName,
        description: phaseDescription,
        due_date: phaseDueDate || null,
        video_url: videoUrl.trim() || null
      });
      
      toast.success('Phase added successfully');
      setShowModal(false);
      setPhaseName('');
      setPhaseDescription('');
      setPhaseDueDate('');
      setVideoUrl('');
      
      // Call the callback to refresh the project data
      if (onPhaseAdded) {
        onPhaseAdded(response.data.phase);
      }
    } catch (error) {
      console.error('Error adding phase:', error);
      toast.error(error.response?.data?.error || 'Failed to add phase');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
        Add Phase
      </button>
      
      {showModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Add New Phase
                      </h3>
                      
                      {nextOrderIndex !== null && (
                        <div className="mt-2 flex items-center text-sm text-blue-600 bg-blue-50 p-2 rounded">
                          <InformationCircleIcon className="h-5 w-5 mr-1" />
                          <span>This phase will be added with order index: {nextOrderIndex}</span>
                        </div>
                      )}
                      
                      <div className="mt-2">
                        <div className="mb-4">
                          <label htmlFor="phaseName" className="block text-sm font-medium text-gray-700">
                            Phase Name *
                          </label>
                          <input
                            type="text"
                            name="phaseName"
                            id="phaseName"
                            value={phaseName}
                            onChange={(e) => setPhaseName(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="Enter phase name"
                            required
                          />
                        </div>
                        <div className="mb-4">
                          <label htmlFor="phaseDescription" className="block text-sm font-medium text-gray-700">
                            Description
                          </label>
                          <textarea
                            id="phaseDescription"
                            name="phaseDescription"
                            rows={3}
                            value={phaseDescription}
                            onChange={(e) => setPhaseDescription(e.target.value)}
                            className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            placeholder="Enter phase description"
                          />
                        </div>
                        <div className="mb-4">
                          <label htmlFor="videoUrl" className="block text-sm font-medium text-gray-700">
                            Video URL
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <VideoCameraIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </div>
                            <input
                              type="url"
                              name="videoUrl"
                              id="videoUrl"
                              value={videoUrl}
                              onChange={(e) => setVideoUrl(e.target.value)}
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                              placeholder="https://example.com/video"
                            />
                          </div>
                          <p className="mt-1 text-sm text-gray-500">
                            Enter a URL to a video that explains this phase
                          </p>
                        </div>
                        <div>
                          <label htmlFor="phaseDueDate" className="block text-sm font-medium text-gray-700">
                            Due Date
                          </label>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <CalendarIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </div>
                            <input
                              type="date"
                              name="phaseDueDate"
                              id="phaseDueDate"
                              value={phaseDueDate}
                              onChange={(e) => setPhaseDueDate(e.target.value)}
                              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={isSubmitting || !phaseName.trim() || loadingOrderIndex}
                    className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm ${
                      (isSubmitting || !phaseName.trim() || loadingOrderIndex) ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? 'Adding...' : 'Add Phase'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddPhaseButton; 